import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { CITIES, CITY_ORDER } from "@/data/cities";
import { PANEL_CITIES, PANEL_CITY_ORDER } from "@/data/panelUpgradeCities";

const VARIANTS = {
  ev: {
    testId: "ev-cities-strip",
    linkPrefix: "home-city-link",
    kicker: "EV Charger Installs by City",
    heading: "We install EV chargers",
    subhead: "across the Lower Mainland.",
    intro: "Every city has its own quirks — panel age, permit desk, strata rules. Pick your city for a page written for local homes.",
    tag: "EV Charger",
    cities: CITIES,
    order: CITY_ORDER,
    serviceSlug: "ev-charger-installation",
    servicePath: "/services/ev-charger-installation",
    serviceLabel: "Main EV Charger service",
    guidePath: "/guides/bc-ev-charger-rebates-2026",
    guideLabel: "2026 BC Rebates Guide",
  },
  panel: {
    testId: "panel-cities-strip",
    linkPrefix: "home-panel-city-link",
    kicker: "Panel Upgrades by City",
    heading: "200-amp panel upgrades,",
    subhead: "permitted and inspected.",
    intro: "Older Lower Mainland homes weren't built for today's electrical load. We upgrade the panel, pull the permit, and close with a BC Safety Authority sign-off.",
    tag: "Panel Upgrade",
    cities: PANEL_CITIES,
    order: PANEL_CITY_ORDER,
    serviceSlug: "panel-upgrades",
    servicePath: "/services/panel-upgrades",
    serviceLabel: "Main Panel Upgrade service",
    guidePath: "/guides/electrical-panel-upgrade-cost-bc-2026",
    guideLabel: "2026 Panel Upgrade Cost Guide",
  },
};

export default function CitiesStrip({ variant = "ev" }) {
  const v = VARIANTS[variant];
  return (
    <section
      id={variant === "ev" ? "ev-cities" : "panel-cities"}
      data-testid={v.testId}
      className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} /> {v.kicker}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[0.95]">
              {v.heading}
              <br />
              <span className="text-white/40">{v.subhead}</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-base md:text-lg text-white/70 leading-relaxed">
            {v.intro}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {v.order.map((s) => (
            <Link
              key={s}
              to={`/services/${v.serviceSlug}/${s}`}
              data-testid={`${v.linkPrefix}-${s}`}
              className="group border border-[#262626] hover:border-[#E60000] p-5 flex flex-col gap-1 transition-colors"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">{v.tag}</div>
              <div className="mt-1 font-display text-xl md:text-2xl font-bold group-hover:text-white transition-colors">
                {v.cities[s].city}
              </div>
              <ArrowUpRight className="w-4 h-4 mt-2 text-white/40 group-hover:text-[#E60000] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to={v.servicePath}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
          >
            {v.serviceLabel} <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
          <Link
            to={v.guidePath}
            className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
          >
            {v.guideLabel} <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
